import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../context/useAuth'
import { useNls } from '../../nls/nlsProvider'
import {
  createEduPracticeLog,
  deleteEduPracticeLog,
  getEduCertificatePdfUrl,
  getEduLearnerProgress,
  listEduCertificates,
  listEduPracticeLogs,
  logEduProgressValue,
  updateEduPracticeLog,
  uploadEduPracticeLogFiles,
  type EduCertificateItem,
  type EduLearnerProgressMetricItem,
  type EduPracticeLog,
  type GetEduLearnerProgressResult,
} from '../../services/eduProgressService'
import {
  Button,
  ConfirmActionModal,
  EmptyHubState,
  FamilyCard,
  FilePicker,
  FileRow,
  formatFileSize,
  ProgressChart,
  SectionLoader,
  Select,
  StatusPill,
  TextInput,
  Textarea,
  Toggle,
} from '../Common'
import { mapEduServerError } from '../Education/classesBuilderHelpers'
import './familyTrainingSection.css'

export interface FamilyTrainingSectionProps {
  workspaceId: string
  learnerId: string
  trainingProgrammes: Array<{ rootId: string; title: string }>
  frozen?: boolean
}

export const FamilyTrainingSection: React.FC<FamilyTrainingSectionProps> = ({
  workspaceId,
  learnerId,
  trainingProgrammes,
  frozen = false,
}) => {
  const { t } = useNls()
  const { currentUser } = useAuth()
  const [selectedRootId, setSelectedRootId] = useState<string>(
    () => trainingProgrammes[0]?.rootId || '',
  )

  // Data states
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [progressData, setProgressData] = useState<GetEduLearnerProgressResult | null>(null)
  const [certificates, setCertificates] = useState<EduCertificateItem[]>([])
  const [practiceLogs, setPracticeLogs] = useState<EduPracticeLog[]>([])
  const [practiceLogsCursor, setPracticeLogsCursor] = useState<string | null>(null)
  const [loadingMoreLogs, setLoadingMoreLogs] = useState<boolean>(false)

  // Action / UI feedback states
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [copiedCertCode, setCopiedCertCode] = useState<string | null>(null)

  // Self-log modal/form state
  const [selfLogMetric, setSelfLogMetric] = useState<EduLearnerProgressMetricItem | null>(null)
  const [selfLogDate, setSelfLogDate] = useState<string>(() => new Date().toISOString().slice(0, 10))
  const [selfLogVal, setSelfLogVal] = useState<string>('')
  const [selfLogPassFail, setSelfLogPassFail] = useState<boolean>(false)
  const [selfLogNote, setSelfLogNote] = useState<string>('')
  const [selfLogSubmitting, setSelfLogSubmitting] = useState<boolean>(false)

  // Add / Edit practice log state
  const [isLogFormOpen, setIsLogFormOpen] = useState<boolean>(false)
  const [editingLogId, setEditingLogId] = useState<string | null>(null)
  const [logDate, setLogDate] = useState<string>(() => new Date().toISOString().slice(0, 10))
  const [logMinutes, setLogMinutes] = useState<string>('30')
  const [logPractised, setLogPractised] = useState<string>('')
  const [logNotes, setLogNotes] = useState<string>('')
  const [logFiles, setLogFiles] = useState<File[]>([])
  const [logMetricValues, setLogMetricValues] = useState<Record<string, number | boolean>>({})
  const [logSubmitting, setLogSubmitting] = useState<boolean>(false)
  const [deletingLog, setDeletingLog] = useState<EduPracticeLog | null>(null)

  const activeProgramme = useMemo(() => {
    return trainingProgrammes.find((p) => p.rootId === selectedRootId) || trainingProgrammes[0] || null
  }, [trainingProgrammes, selectedRootId])

  useEffect(() => {
    if (!selectedRootId) return

    let active = true

    Promise.all([
      getEduLearnerProgress({ workspaceId, rootId: selectedRootId, learnerId }),
      listEduCertificates({ workspaceId, learnerId, rootId: selectedRootId }).catch(() => ({ certificates: [] })),
      listEduPracticeLogs({ workspaceId, rootId: selectedRootId, learnerId }).catch(() => ({ logs: [], nextCursor: null })),
    ])
      .then(([progResult, certsResult, logsResult]) => {
        if (!active) return
        setProgressData(progResult)
        setCertificates(certsResult.certificates || [])
        setPracticeLogs(logsResult.logs || [])
        setPracticeLogsCursor(logsResult.nextCursor || null)
        setError(null)
      })
      .catch((err: unknown) => {
        if (!active) return
        setError(mapEduServerError(err, t))
      })
      .finally(() => {
        if (active) {
          setLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [workspaceId, learnerId, selectedRootId, t])

  const handleLoadMoreLogs = async () => {
    if (!practiceLogsCursor || !selectedRootId || loadingMoreLogs) return
    setLoadingMoreLogs(true)
    try {
      const res = await listEduPracticeLogs({
        workspaceId,
        rootId: selectedRootId,
        learnerId,
        cursor: practiceLogsCursor,
      })
      setPracticeLogs((prev) => [...prev, ...(res.logs || [])])
      setPracticeLogsCursor(res.nextCursor || null)
    } catch (err: unknown) {
      setActionError(mapEduServerError(err, t))
    } finally {
      setLoadingMoreLogs(false)
    }
  }

  // Self-log handlers
  const openSelfLog = (metric: EduLearnerProgressMetricItem) => {
    setSelfLogMetric(metric)
    setSelfLogDate(new Date().toISOString().slice(0, 10))
    setSelfLogVal('')
    setSelfLogPassFail(false)
    setSelfLogNote('')
    setActionError(null)
    setActionSuccess(null)
  }

  const handleSelfLogSubmit = async () => {
    if (!selfLogMetric || !selectedRootId || selfLogSubmitting) return
    const today = new Date().toISOString().slice(0, 10)
    if (selfLogDate > today) {
      setActionError(t('NLSFamilyTrainingPracticeDateFuture'))
      return
    }

    let valueToLog: number
    if (selfLogMetric.kind === 'PASS_FAIL') {
      valueToLog = selfLogPassFail ? 1 : 0
    } else {
      const parsed = Number(selfLogVal)
      if (Number.isNaN(parsed)) {
        setActionError(t('NLSEducationBuilderErrorInvalidArgument'))
        return
      }
      valueToLog = parsed
    }

    setSelfLogSubmitting(true)
    setActionError(null)
    try {
      await logEduProgressValue({
        workspaceId,
        rootId: selectedRootId,
        metricId: selfLogMetric.metricId,
        learnerId,
        value: valueToLog,
        date: selfLogDate,
        note: selfLogNote.trim() || undefined,
      })
      setActionSuccess(t('NLSFamilyTrainingSelfLogSuccess'))
      setSelfLogMetric(null)
      // Reload progress to show the new pending value
      const updated = await getEduLearnerProgress({ workspaceId, rootId: selectedRootId, learnerId })
      setProgressData(updated)
    } catch (err: unknown) {
      setActionError(mapEduServerError(err, t))
    } finally {
      setSelfLogSubmitting(false)
    }
  }

  // Certificate PDF & verify link handlers
  const handleOpenPdf = async (certificateId: string) => {
    try {
      const res = await getEduCertificatePdfUrl({ workspaceId, certificateId })
      if (res.url) {
        window.open(res.url, '_blank', 'noopener,noreferrer')
      } else {
        setActionError(t('NLSEduCertPdfUrlFailed'))
      }
    } catch (err: unknown) {
      setActionError(mapEduServerError(err, t))
    }
  }

  const handleCopyVerifyLink = (code: string) => {
    const url = `${window.location.origin}/verify/${code}`
    void navigator.clipboard?.writeText(url).then(() => {
      setCopiedCertCode(code)
      setTimeout(() => setCopiedCertCode(null), 3000)
    })
  }

  // Add / Edit practice log handlers
  const openAddLogForm = () => {
    setEditingLogId(null)
    setLogDate(new Date().toISOString().slice(0, 10))
    setLogMinutes('30')
    setLogPractised('')
    setLogNotes('')
    setLogFiles([])
    setLogMetricValues({})
    setIsLogFormOpen(true)
    setActionError(null)
    setActionSuccess(null)
  }

  const openEditLogForm = (log: EduPracticeLog) => {
    setEditingLogId(log.id)
    setLogDate(log.date)
    setLogMinutes(String(log.minutes))
    setLogPractised(log.practised)
    setLogNotes(log.notes || '')
    setLogFiles([])
    setLogMetricValues({})
    setIsLogFormOpen(true)
    setActionError(null)
    setActionSuccess(null)
  }

  const handleLogFormSubmit = async () => {
    if (!selectedRootId || logSubmitting) return
    const today = new Date().toISOString().slice(0, 10)
    if (logDate > today) {
      setActionError(t('NLSFamilyTrainingPracticeDateFuture'))
      return
    }

    const minutesNum = Number(logMinutes)
    if (!Number.isInteger(minutesNum) || minutesNum < 1 || minutesNum > 1440) {
      setActionError(t('NLSFamilyTrainingPracticeMinutesError'))
      return
    }

    const trimmedPractised = logPractised.trim()
    if (!trimmedPractised || trimmedPractised.length > 500) {
      setActionError(t('NLSFamilyTrainingPractisedRequired'))
      return
    }

    if (logFiles.length > 5) {
      setActionError(t('NLSFamilyTrainingPracticeMaxFiles'))
      return
    }

    setLogSubmitting(true)
    setActionError(null)

    try {
      // Build metricValues array if any were populated
      const metricValuesPayload: Array<{ metricId: string; value: number }> = []
      Object.entries(logMetricValues).forEach(([mId, val]) => {
        if (typeof val === 'boolean') {
          metricValuesPayload.push({ metricId: mId, value: val ? 1 : 0 })
        } else if (typeof val === 'number' && !Number.isNaN(val)) {
          metricValuesPayload.push({ metricId: mId, value: val })
        }
      })

      if (editingLogId) {
        await updateEduPracticeLog({
          workspaceId,
          logId: editingLogId,
          minutes: minutesNum,
          practised: trimmedPractised,
          notes: logNotes.trim() || undefined,
        })
        setActionSuccess(t('NLSFamilyTrainingPracticeSaveSuccess'))
      } else {
        let fileIds: string[] = []
        if (logFiles.length > 0) {
          fileIds = await uploadEduPracticeLogFiles({
            workspaceId,
            rootId: selectedRootId,
            learnerId,
            files: logFiles,
          })
        }

        const requestId = typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `req-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

        await createEduPracticeLog({
          workspaceId,
          rootId: selectedRootId,
          learnerId,
          date: logDate,
          minutes: minutesNum,
          practised: trimmedPractised,
          notes: logNotes.trim() || undefined,
          fileIds: fileIds.length > 0 ? fileIds : undefined,
          metricValues: metricValuesPayload.length > 0 ? metricValuesPayload : undefined,
          requestId,
        })
        setActionSuccess(t('NLSFamilyTrainingPracticeSaveSuccess'))
      }

      setIsLogFormOpen(false)
      // Reload logs and progress
      const [logsRes, progRes] = await Promise.all([
        listEduPracticeLogs({ workspaceId, rootId: selectedRootId, learnerId }),
        getEduLearnerProgress({ workspaceId, rootId: selectedRootId, learnerId }),
      ])
      setPracticeLogs(logsRes.logs || [])
      setPracticeLogsCursor(logsRes.nextCursor || null)
      setProgressData(progRes)
    } catch (err: unknown) {
      setActionError(mapEduServerError(err, t))
    } finally {
      setLogSubmitting(false)
    }
  }

  const handleDeleteLogConfirm = async () => {
    if (!deletingLog) return
    try {
      await deleteEduPracticeLog({
        workspaceId,
        logId: deletingLog.id,
      })
      setActionSuccess(t('NLSFamilyTrainingPracticeDeleteSuccess'))
      setDeletingLog(null)
      const [logsRes, progRes] = await Promise.all([
        listEduPracticeLogs({ workspaceId, rootId: selectedRootId, learnerId }),
        getEduLearnerProgress({ workspaceId, rootId: selectedRootId, learnerId }),
      ])
      setPracticeLogs(logsRes.logs || [])
      setPracticeLogsCursor(logsRes.nextCursor || null)
      setProgressData(progRes)
    } catch (err: unknown) {
      setActionError(mapEduServerError(err, t))
      setDeletingLog(null)
    }
  }

  const selfLoggableMetrics = useMemo(() => {
    return progressData?.metrics.filter((m) => m.selfLoggable) || []
  }, [progressData?.metrics])

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), [])

  return (
    <section className="familyTrainingSection" data-testid="family-training-section">
      <div className="familyTrainingSection__header">
        <h3 className="familyTrainingSection__title">{t('NLSFamilyTrainingHeading')}</h3>
        {trainingProgrammes.length > 1 && (
          <div className="familyTrainingSection__picker">
            <Select<string>
              value={selectedRootId}
              options={trainingProgrammes.map((p) => ({
                value: p.rootId,
                label: p.title,
              }))}
              onChange={setSelectedRootId}
              ariaLabel={t('NLSFamilyTrainingSelectProgramme')}
            />
          </div>
        )}
      </div>

      {actionError && (
        <div className="familyTrainingSection__error" role="alert">
          {actionError}
        </div>
      )}

      {actionSuccess && (
        <div
          className="familyTrainingSection__success"
          role="status"
          style={{
            padding: 'var(--space-3, 12px)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface-muted)',
            border: '1px solid var(--border-subtle)',
            color: 'var(--text-strong)',
            fontSize: 'var(--font-size-sm, 0.875rem)',
          }}
        >
          {actionSuccess}
        </div>
      )}

      {loading && <SectionLoader />}

      {!loading && error && (
        <FamilyCard title={activeProgramme?.title || t('NLSFamilyTrainingHeading')}>
          <div className="familyTrainingSection__error">{error}</div>
        </FamilyCard>
      )}

      {!loading && !error && progressData && (
        <>
          {/* Level & Next Level Criteria Card */}
          <FamilyCard title={activeProgramme?.title || t('NLSFamilyTrainingHeading')}>
            <div className="familyTrainingLevelCard__content">
              <div className="familyTrainingLevelCard__current">
                {progressData.currentLevel ? (
                  <>
                    <span
                      className="familyTrainingLevelCard__dot"
                      style={{
                        backgroundColor: progressData.currentLevel.colour || 'var(--accent)',
                      }}
                      aria-hidden="true"
                    />
                    <strong>
                      {t('NLSFamilyTrainingCurrentLevel', { level: progressData.currentLevel.name })}
                    </strong>
                    {progressData.currentLevel.since && (
                      <span className="familyTrainingLevelCard__since">
                        {t('NLSFamilyTrainingLevelSince', { date: progressData.currentLevel.since })}
                      </span>
                    )}
                  </>
                ) : (
                  <span>{t('NLSFamilyTrainingNoLevel')}</span>
                )}
              </div>

              {progressData.nextLevel && (
                <div className="familyTrainingLevelCard__next">
                  <h4 className="familyTrainingLevelCard__nextTitle">
                    {t('NLSFamilyTrainingNextLevelCriteria', {
                      level: progressData.nextLevel.name,
                    })}
                  </h4>
                  <ul className="familyTrainingLevelCard__criteriaList">
                    {progressData.nextLevel.criteriaEvaluation.attendance && (
                      <li className="familyTrainingLevelCard__criterion">
                        <span>
                          {t('NLSEduTraineeCriteriaAttendance', {
                            actual: progressData.nextLevel.criteriaEvaluation.attendance.actualPercent ?? 0,
                            target: progressData.nextLevel.criteriaEvaluation.attendance.minPercent,
                          })}
                        </span>
                        <StatusPill
                          tone={progressData.nextLevel.criteriaEvaluation.attendance.met ? 'success' : 'neutral'}
                          compact
                        >
                          {progressData.nextLevel.criteriaEvaluation.attendance.met
                            ? t('NLSFamilyTrainingCriteriaMet')
                            : t('NLSFamilyTrainingCriteriaInProgress')}
                        </StatusPill>
                      </li>
                    )}
                    {progressData.nextLevel.criteriaEvaluation.metricTargets?.map((crit) => {
                      const critMetric = progressData.metrics.find((m) => m.metricId === crit.metricId)
                      const unit = critMetric?.unit ? ` ${critMetric.unit}` : ''
                      const metricName = crit.metricName || critMetric?.name || t('NLSFamilyTrainingCriteriaMetricFallback')
                      return (
                        <li key={crit.metricId} className="familyTrainingLevelCard__criterion">
                          <span>
                            {t('NLSFamilyTrainingCriteriaMetricItem', {
                              metric: metricName,
                              actual: crit.actual ?? 0,
                              target: crit.target,
                              unit,
                            })}
                          </span>
                          <StatusPill tone={crit.met ? 'success' : 'neutral'} compact>
                            {crit.met
                              ? t('NLSFamilyTrainingCriteriaMet')
                              : t('NLSFamilyTrainingCriteriaInProgress')}
                          </StatusPill>
                        </li>
                      )
                    })}
                    {progressData.nextLevel.criteriaEvaluation.minDaysAtPrevious && (
                      <li className="familyTrainingLevelCard__criterion">
                        <span>
                          {t('NLSEduTraineeCriteriaDays', {
                            actual: progressData.nextLevel.criteriaEvaluation.minDaysAtPrevious.actualDays ?? 0,
                            target: progressData.nextLevel.criteriaEvaluation.minDaysAtPrevious.minDays,
                          })}
                        </span>
                        <StatusPill
                          tone={progressData.nextLevel.criteriaEvaluation.minDaysAtPrevious.met ? 'success' : 'neutral'}
                          compact
                        >
                          {progressData.nextLevel.criteriaEvaluation.minDaysAtPrevious.met
                            ? t('NLSFamilyTrainingCriteriaMet')
                            : t('NLSFamilyTrainingCriteriaInProgress')}
                        </StatusPill>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </FamilyCard>

          {/* Metric Charts */}
          {progressData.metrics.length > 0 && (
            <div className="familyTrainingMetricsGrid">
              {progressData.metrics.map((metric) => (
                <div key={metric.metricId} className="familyTrainingMetricCard">
                  <div className="familyTrainingMetricCard__header">
                    <h4 className="familyTrainingMetricCard__title">{metric.name}</h4>
                    {metric.target !== undefined && metric.target !== null && (
                      <span className="familyTrainingMetricCard__target">
                        {t('NLSEduTraineeMetricTargetValue', {
                          target: metric.target,
                          unit: metric.unit || '',
                        })}
                      </span>
                    )}
                    {!frozen && metric.selfLoggable && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => openSelfLog(metric)}
                      >
                        {t('NLSFamilyTrainingSelfLogBtn')}
                      </Button>
                    )}
                  </div>
                  {metric.series.length === 0 ? (
                    <div className="familyTrainingMetricCard__empty">
                      {t('NLSEduTraineeMetricNoValues')}
                    </div>
                  ) : (
                    <ProgressChart
                      series={metric.series.map((s) => ({
                        date: s.date,
                        value: s.value,
                        status: s.status === 'SELF_REPORTED' ? 'SELF_REPORTED' : 'CONFIRMED',
                      }))}
                      kind={metric.kind === 'COUNT' || metric.kind === 'PASS_FAIL' ? 'bar' : 'line'}
                      unit={metric.unit}
                      target={metric.target}
                      direction={metric.direction}
                      ariaLabel={t('NLSEduTraineeChartAria', { name: metric.name })}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Self-log inline form */}
          {!frozen && selfLogMetric && (
            <FamilyCard
              title={t('NLSFamilyTrainingSelfLogTitle', { metric: selfLogMetric.name })}
            >
              <div className="familyTrainingSelfLogForm">
                <div className="familyTrainingSelfLogForm__field">
                  <label className="familyTrainingSelfLogForm__label">
                    {t('NLSFamilyTrainingSelfLogDate')}
                  </label>
                  <TextInput
                    type="date"
                    value={selfLogDate}
                    onChange={setSelfLogDate}
                    aria-label={t('NLSFamilyTrainingSelfLogDate')}
                  />
                </div>

                <div className="familyTrainingSelfLogForm__field">
                  <label className="familyTrainingSelfLogForm__label">
                    {t('NLSFamilyTrainingSelfLogValue')}
                  </label>
                  {selfLogMetric.kind === 'PASS_FAIL' ? (
                    <Toggle
                      checked={selfLogPassFail}
                      onChange={setSelfLogPassFail}
                      label={t('NLSFamilyTrainingSelfLogPassFail')}
                    />
                  ) : (
                    <TextInput
                      type="number"
                      value={selfLogVal}
                      onChange={setSelfLogVal}
                      aria-label={t('NLSFamilyTrainingSelfLogValue')}
                    />
                  )}
                </div>

                <div className="familyTrainingSelfLogForm__field">
                  <label className="familyTrainingSelfLogForm__label">
                    {t('NLSFamilyTrainingSelfLogNote')}
                  </label>
                  <TextInput
                    value={selfLogNote}
                    onChange={setSelfLogNote}
                    placeholder={t('NLSFamilyTrainingSelfLogNotePlaceholder')}
                    aria-label={t('NLSFamilyTrainingSelfLogNote')}
                  />
                </div>

                <div className="familyTrainingSelfLogForm__actions">
                  <Button
                    size="sm"
                    variant="tertiary"
                    disabled={selfLogSubmitting}
                    onClick={() => setSelfLogMetric(null)}
                  >
                    {t('NLSFamilyTrainingSelfLogCancelBtn')}
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={selfLogSubmitting}
                    loading={selfLogSubmitting}
                    onClick={handleSelfLogSubmit}
                  >
                    {t('NLSFamilyTrainingSelfLogSaveBtn')}
                  </Button>
                </div>
              </div>
            </FamilyCard>
          )}

          {/* Certificates Card */}
          <FamilyCard title={t('NLSFamilyTrainingCertificatesHeading')}>
            {certificates.length === 0 ? (
              <EmptyHubState
                title={t('NLSFamilyTrainingNoCertificates')}
                variant="default"
              />
            ) : (
              <table className="familyTrainingTable" data-testid="family-certificates-table">
                <thead>
                  <tr>
                    <th>{t('NLSEduCertColTitle')}</th>
                    <th>{t('NLSEduCertColLevel')}</th>
                    <th>{t('NLSEduCertColIssueDate')}</th>
                    <th>{t('NLSEduCertColExpiryDate')}</th>
                    <th>{t('NLSEduCertColStatus')}</th>
                    <th>{t('NLSEduCertColActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {certificates.map((cert) => {
                    const isExpired =
                      cert.status === 'ISSUED' && Boolean(cert.expiryDate && cert.expiryDate < todayStr)
                    const titleText = cert.snapshot?.rendered?.title || cert.id
                    const levelName = cert.snapshot?.level?.name || cert.levelId || '-'

                    return (
                      <tr key={cert.id} data-testid={`edu-cert-row-${cert.id}`}>
                        <td>{titleText}</td>
                        <td>{levelName}</td>
                        <td>{cert.issueDate}</td>
                        <td>{cert.expiryDate || t('NLSVerifyCertificateNoExpiry')}</td>
                        <td>
                          {cert.status === 'REVOKED' ? (
                            <StatusPill tone="danger" compact>
                              {t('NLSEduCertStatusRevoked')}
                            </StatusPill>
                          ) : cert.status === 'SUPERSEDED' ? (
                            <StatusPill tone="neutral" compact>
                              {t('NLSEduCertStatusSuperseded')}
                            </StatusPill>
                          ) : isExpired ? (
                            <StatusPill tone="warning" compact>
                              {t('NLSEduCertStatusExpired')}
                            </StatusPill>
                          ) : (
                            <StatusPill tone="success" compact>
                              {t('NLSEduCertStatusIssued')}
                            </StatusPill>
                          )}
                        </td>
                        <td>
                          <div className="familyTrainingCertRow__actions">
                            {cert.pdfStatus === 'READY' && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleOpenPdf(cert.id)}
                              >
                                {t('NLSEduCertOpenPdfBtn')}
                              </Button>
                            )}
                            {cert.pdfStatus === 'PENDING' && (
                              <span>{t('NLSEduCertPdfPending')}</span>
                            )}
                            {cert.pdfStatus === 'FAILED' && (
                              <span>{t('NLSEduCertPdfFailed')}</span>
                            )}
                            <Button
                              size="sm"
                              variant="tertiary"
                              onClick={() => handleCopyVerifyLink(cert.code)}
                            >
                              {t('NLSFamilyTrainingCopyVerifyLink')}
                            </Button>
                            {copiedCertCode === cert.code && (
                              <span className="familyTrainingCertRow__copied">
                                {t('NLSFamilyTrainingLinkCopied')}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </FamilyCard>

          {/* Practice Logs Card */}
          <FamilyCard
            title={t('NLSFamilyTrainingPracticeLogsHeading')}
            actions={
              !frozen ? (
                <Button size="sm" variant="primary" onClick={openAddLogForm}>
                  {t('NLSFamilyTrainingAddPracticeLogBtn')}
                </Button>
              ) : undefined
            }
          >
            {/* Add / Edit Form */}
            {!frozen && isLogFormOpen && (
              <div className="familyTrainingPracticeLogForm" data-testid="practice-log-form">
                <div className="familyTrainingPracticeLogForm__row">
                  <div className="familyTrainingPracticeLogForm__field">
                    <label className="familyTrainingPracticeLogForm__label">
                      {t('NLSFamilyTrainingPracticeDate')}
                    </label>
                    <TextInput
                      type="date"
                      value={logDate}
                      onChange={setLogDate}
                      aria-label={t('NLSFamilyTrainingPracticeDate')}
                    />
                  </div>
                  <div className="familyTrainingPracticeLogForm__field">
                    <label className="familyTrainingPracticeLogForm__label">
                      {t('NLSFamilyTrainingPracticeMinutes')}
                    </label>
                    <TextInput
                      type="number"
                      value={logMinutes}
                      onChange={setLogMinutes}
                      aria-label={t('NLSFamilyTrainingPracticeMinutes')}
                    />
                  </div>
                </div>

                <div className="familyTrainingPracticeLogForm__field">
                  <label className="familyTrainingPracticeLogForm__label">
                    {t('NLSFamilyTrainingPractisedWhat')}
                  </label>
                  <TextInput
                    value={logPractised}
                    onChange={setLogPractised}
                    placeholder={t('NLSFamilyTrainingPractisedPlaceholder')}
                    aria-label={t('NLSFamilyTrainingPractisedWhat')}
                  />
                </div>

                <div className="familyTrainingPracticeLogForm__field">
                  <label className="familyTrainingPracticeLogForm__label">
                    {t('NLSFamilyTrainingPracticeNotes')}
                  </label>
                  <Textarea
                    value={logNotes}
                    onChange={setLogNotes}
                    placeholder={t('NLSFamilyTrainingPracticeNotesPlaceholder')}
                    aria-label={t('NLSFamilyTrainingPracticeNotes')}
                  />
                </div>

                {!editingLogId && (
                  <div className="familyTrainingPracticeLogForm__field">
                    <label className="familyTrainingPracticeLogForm__label">
                      {t('NLSFamilyTrainingPracticeFiles')}
                    </label>
                    <FilePicker
                      accept="image/*,video/*,audio/*"
                      multiple
                      maxBytes={20 * 1024 * 1024}
                      disabled={logSubmitting || logFiles.length >= 5}
                      onFiles={(newFiles) => {
                        setLogFiles((prev) => [...prev, ...newFiles].slice(0, 5))
                      }}
                    />
                    {logFiles.length > 0 && (
                      <div className="familyTrainingPracticeLogForm__files">
                        {logFiles.map((f, idx) => (
                          <FileRow
                            key={`${f.name}_${idx}`}
                            name={f.name}
                            sizeBytes={f.size}
                            onRemove={() => setLogFiles((prev) => prev.filter((_, i) => i !== idx))}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {selfLoggableMetrics.length > 0 && (
                  <div className="familyTrainingPracticeLogForm__metrics">
                    <strong>{t('NLSFamilyTrainingPracticeMetricValues')}</strong>
                    {selfLoggableMetrics.map((metric) => (
                      <div key={metric.metricId} className="familyTrainingPracticeLogForm__field">
                        <label className="familyTrainingPracticeLogForm__label">
                          {metric.name}
                        </label>
                        {metric.kind === 'PASS_FAIL' ? (
                          <Toggle
                            checked={Boolean(logMetricValues[metric.metricId])}
                            onChange={(checked) =>
                              setLogMetricValues((prev) => ({ ...prev, [metric.metricId]: checked }))
                            }
                            label={t('NLSFamilyTrainingSelfLogPassFail')}
                          />
                        ) : (
                          <TextInput
                            type="number"
                            value={
                              logMetricValues[metric.metricId] !== undefined
                                ? String(logMetricValues[metric.metricId])
                                : ''
                            }
                            onChange={(val) =>
                              setLogMetricValues((prev) => ({
                                ...prev,
                                [metric.metricId]: Number(val),
                              }))
                            }
                            aria-label={metric.name}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="familyTrainingPracticeLogForm__actions">
                  <Button
                    size="sm"
                    variant="tertiary"
                    disabled={logSubmitting}
                    onClick={() => setIsLogFormOpen(false)}
                  >
                    {t('NLSFamilyTrainingSelfLogCancelBtn')}
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={logSubmitting}
                    loading={logSubmitting}
                    onClick={handleLogFormSubmit}
                  >
                    {t('NLSFamilyTrainingPracticeSaveBtn')}
                  </Button>
                </div>
              </div>
            )}

            {/* Logs List */}
            {practiceLogs.length === 0 ? (
              <EmptyHubState
                title={t('NLSFamilyTrainingNoPracticeLogs')}
                variant="default"
              />
            ) : (
              <div className="familyTrainingLogsList" data-testid="practice-logs-list">
                {practiceLogs.map((log) => {
                  const canEdit = !frozen && currentUser?.uid && log.createdBy === currentUser.uid

                  return (
                    <article key={log.id} className="familyTrainingLogCard" data-testid={`practice-log-item-${log.id}`}>
                      <div className="familyTrainingLogCard__header">
                        <div className="familyTrainingLogCard__meta">
                          <span className="familyTrainingLogCard__date">{log.date}</span>
                          <span className="familyTrainingLogCard__minutes">
                            {t('NLSEduPracticeLogDuration', { minutes: log.minutes })}
                          </span>
                          {log.focusSessionId && (
                            <StatusPill tone="neutral" compact>
                              {t('NLSEduPracticeLogFocusChip')}
                            </StatusPill>
                          )}
                        </div>

                        {canEdit && (
                          <div className="familyTrainingLogCard__actions">
                            <Button
                              size="sm"
                              variant="tertiary"
                              onClick={() => openEditLogForm(log)}
                            >
                              {t('NLSFamilyTrainingPracticeEditBtn')}
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => setDeletingLog(log)}
                            >
                              {t('NLSFamilyTrainingPracticeDeleteBtn')}
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="familyTrainingLogCard__practised">
                        {log.practised}
                      </div>

                      {log.notes && (
                        <p className="familyTrainingLogCard__notes">{log.notes}</p>
                      )}

                      {/* Attachments */}
                      {log.attachments && log.attachments.length > 0 && (
                        <ul className="familyTrainingPracticeLog__attachments">
                          {log.attachments.map((att) => (
                            <li key={att.id}>
                              <a
                                href={att.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="familyTrainingPracticeLog__attachmentLink"
                              >
                                {att.name} ({formatFileSize(att.size)})
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Comments */}
                      {log.comments && log.comments.length > 0 && (
                        <div className="familyTrainingPracticeLog__comments">
                          <strong>{t('NLSEduPracticeLogCommentsHeader')}</strong>
                          {log.comments.map((comment) => (
                            <div key={comment.id} className="familyTrainingPracticeLog__comment">
                              <div className="familyTrainingPracticeLog__commentMeta">
                                <span className="familyTrainingPracticeLog__commentAuthor">
                                  {comment.byName}
                                </span>
                                <span className="familyTrainingPracticeLog__commentDate">
                                  {comment.at}
                                </span>
                              </div>
                              <p style={{ margin: 0 }}>{comment.text}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </article>
                  )
                })}

                {practiceLogsCursor && (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={loadingMoreLogs}
                    disabled={loadingMoreLogs}
                    onClick={handleLoadMoreLogs}
                  >
                    {t('NLSEduPracticeLogsLoadMore')}
                  </Button>
                )}
              </div>
            )}
          </FamilyCard>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deletingLog && (
        <ConfirmActionModal
          open={true}
          title={t('NLSFamilyTrainingPracticeDeleteTitle')}
          body={t('NLSFamilyTrainingPracticeDeleteBody')}
          confirmLabel={t('NLSFamilyTrainingPracticeDeleteConfirm')}
          danger={true}
          onConfirm={handleDeleteLogConfirm}
          onClose={() => setDeletingLog(null)}
        />
      )}
    </section>
  )
}
